import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Style.css';
import ApiSender from '../../common/ApiLogic';
import useLanguage from '../../common/useLanguage';
import { SELFCARE_APP_NAME, CALL_CENTER_USSD, MANAGE_USSD, UNSUB_LINK } from '../../common/constants';
import Header from './components/Header';
import SiteFooter from './components/Footer';

const REVEAL_DELAY_MS = 5000;

// Pulls the 9-digit local part (55/99 prefix) out of either a bare local number
// or a full 12-digit MSISDN (996XXXXXXXXX), the way it would arrive from a deep link.
function normalizeLocalMsisdn(raw) {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const local = digits.startsWith('996') ? digits.slice(3) : digits;
  return local.slice(0, 9);
}

function statusMessage(lang, key) {
  const MESSAGES = {
    active: { RU: 'Подписка Kid Security активна', KY: 'Kid Security жазылуусу активдүү', EN: 'Kid Security subscription is active' },
    inactive: { RU: 'Активная подписка не найдена', KY: 'Активдүү жазылуу табылган жок', EN: 'No active subscription found' },
    error: { RU: 'Произошла ошибка, попробуйте позже', KY: 'Ката кетти, кийинчерээк аракет кылыңыз', EN: 'Something went wrong, please try again later' },
  };
  return MESSAGES[key][lang] || MESSAGES[key].EN;
}

function UnsubPage() {
  const location = useLocation();
  const apiSender = useMemo(() => new ApiSender(), []);
  const { language, selectLanguage, t, paramsObject } = useLanguage(location, apiSender);

  const [msisdn, setMsisdn] = useState('');
  const [isMsisdnFocused, setIsMsisdnFocused] = useState(false);
  const [warning, setWarning] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const [firstStep, setFirstStep] = useState(true);
  const [secondStep, setSecondStep] = useState(false);
  const [active, setActive] = useState(false);

  const [revealPending, setRevealPending] = useState(false);
  const [unsubscribeRevealed, setUnsubscribeRevealed] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmSucceeded, setConfirmSucceeded] = useState(false);

  const isMsisdnValid = useCallback((value) => /^(55|99)\d{7}$/.test(value), []);

  const runStatusCheck = useCallback(async (localMsisdn, lang) => {
    setWarning(false);
    setMessage('');
    setLoading(true);

    try {
      const response = await apiSender.status({ msisdn: `996${localMsisdn}`, language: lang });
      setFirstStep(false);
      setSecondStep(true);

      if (apiSender.isSuccess(response)) {
        setActive(true);
        setMessage(response.result?.message || statusMessage(lang, 'active'));
      } else {
        setActive(false);
        setMessage(response.result?.message || statusMessage(lang, 'inactive'));
      }
    } catch (error) {
      console.error('Status check failed:', error);
      setFirstStep(false);
      setSecondStep(true);
      setActive(false);
      setMessage(statusMessage(lang, 'error'));
    } finally {
      setLoading(false);
    }
  }, [apiSender]);

  useEffect(() => {
    const deepLinkMsisdn = normalizeLocalMsisdn(paramsObject.msisdn);
    if (deepLinkMsisdn && isMsisdnValid(deepLinkMsisdn)) {
      setMsisdn(deepLinkMsisdn);
      runStatusCheck(deepLinkMsisdn, language);
    }
    // Deep-link check only ever needs to run once, right after the language is resolved.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMsisdnChange = (event) => {
    setMsisdn(event.target.value.replace(/\D/g, '').slice(0, 9));
    setWarning(false);
    setMessage('');
  };

  const handleMsisdnFocus = () => setIsMsisdnFocused(true);
  const handleMsisdnBlur = () => setIsMsisdnFocused(false);

  const handleCheckStatus = () => {
    if (!isMsisdnValid(msisdn)) {
      setWarning(true);
      setMessage(t('Неверный формат номера', 'Номердин форматы туура эмес', 'Invalid phone number format'));
      return;
    }

    runStatusCheck(msisdn, language);
  };

  const showUnsubscribeReveal = () => {
    setRevealPending(true);
    setTimeout(() => {
      setUnsubscribeRevealed(true);
      setRevealPending(false);
    }, REVEAL_DELAY_MS);
  };

  const openConfirmPopup = () => {
    setShowPopup(true);
    setConfirmMessage('');
    setConfirmSucceeded(false);
  };

  const cancelUnsubscribe = () => {
    if (confirmLoading) return;
    setShowPopup(false);
    setConfirmMessage('');
  };

  const confirmUnsubscribe = async () => {
    if (confirmLoading || confirmSucceeded) return;

    setConfirmLoading(true);
    setConfirmMessage('');

    try {
      const response = await apiSender.unsubscribe({ msisdn: `996${msisdn}`, language });

      setConfirmMessage(
        response.result?.message ||
        (apiSender.isSuccess(response)
          ? t('Подписка отменена', 'Жазылуу жокко чыгарылды', 'Subscription cancelled')
          : t('Не удалось отменить подписку', 'Жазылууну жокко чыгаруу мүмкүн болгон жок', 'Could not cancel the subscription'))
      );
      setConfirmSucceeded(apiSender.isSuccess(response));
    } catch (error) {
      console.error('Unsubscribe failed:', error);
      setConfirmMessage(t('Произошла ошибка, попробуйте позже', 'Ката кетти, кийинчерээк аракет кылыңыз', 'Something went wrong, please try again later'));
    } finally {
      setConfirmLoading(false);
    }
  };

  return (
    <div className="Ks-page">
      <Header language={language} onSelectLanguage={selectLanguage} />

      <div className="container standalone-page">
        <div className="form-content">
          {firstStep && (
            <div className="form-content-middle">
              <div className="status-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="#23c11d" strokeWidth="1.8" />
                  <rect x="9" y="8" width="2" height="8" rx="1" fill="#23c11d" />
                  <rect x="13" y="8" width="2" height="8" rx="1" fill="#23c11d" />
                </svg>
              </div>
              <div className="title">
                <h4 id="headup-title">{t('Введите номер телефона', 'Телефон номериңизди киргизиңиз', 'Enter your phone number')}</h4>
              </div>
              <div className="unsub-phone-group">
                <div className="phone-field">
                  <div className="phone-field-icon"><img alt="" src="/phone-icon.avif" /></div>
                  <div id="msisdn-input" className={`input ${warning ? 'vibrate' : ''}`}>
                    <span id="country-code" className="country-code" style={{ color: isMsisdnFocused || msisdn ? 'black' : '#999' }}>+996</span>
                    <span className="input-divider" />
                    <input id="msisdn-val" type="tel" value={msisdn} placeholder="55X XXX XXX" autoComplete="off" minLength={9} maxLength={9} required onChange={handleMsisdnChange} onFocus={handleMsisdnFocus} onBlur={handleMsisdnBlur} />
                  </div>
                </div>
                <div className={`warning-msg ${warning ? 'active' : ''}`}>
                  <span>{message || ' '}</span>
                </div>
                <div className="kick-span">
                  <button onClick={handleCheckStatus} disabled={loading}>
                    {loading
                      ? t('Проверка...', 'Текшерилүүдө...', 'Checking...')
                      : t('Отменить подписку', 'Жазылуудан баш тартуу', 'Cancel subscription')}
                  </button>
                </div>
              </div>

              <div className="alt-channels">
                <h5>{t('Другие способы отменить подписку', 'Жазылуудан баш тартуунун башка жолдору', 'Other ways to cancel')}</h5>
                <ul>
                  <li>{t(`Личный кабинет: приложение ${SELFCARE_APP_NAME}`, `Жеке кабинет: ${SELFCARE_APP_NAME} колдонмосу`, `Self-service: the ${SELFCARE_APP_NAME} app`)}</li>
                  <li>{t(`Звонок в контакт-центр: ${CALL_CENTER_USSD}`, `Байланыш борборуна чалуу: ${CALL_CENTER_USSD}`, `Call the contact center: ${CALL_CENTER_USSD}`)}</li>
                  <li>{t(`Управление подпиской по USSD: ${MANAGE_USSD}`, `Жазылууну USSD аркылуу башкаруу: ${MANAGE_USSD}`, `Manage your subscription via USSD: ${MANAGE_USSD}`)}</li>
                  <li><a href={UNSUB_LINK} target="_blank" rel="noopener noreferrer">{UNSUB_LINK.replace('https://', '')}</a></li>
                </ul>
              </div>
            </div>
          )}

          {secondStep && (
            <div className="unsub-form">
              {active ? (
                <div>
                  <div className="title">
                    <h4 id="headup-title">{t(`Добро пожаловать, +996 ${msisdn}`, `Кош келиңиз, +996 ${msisdn}`, `Welcome, +996 ${msisdn}`)}</h4>
                  </div>

                  <div className="subscription-card">
                    <div className="subscription-card-top">
                      <span className="subscription-card-service">
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 3 4 6.5V11c0 5 3.4 8.9 8 10 4.6-1.1 8-5 8-10V6.5L12 3Z" stroke="#23c11d" strokeWidth="1.8" strokeLinejoin="round" />
                        </svg>
                        Kid Security
                      </span>
                      <span className="status-pill status-pill-active">{t('Активна', 'Активдүү', 'Active')}</span>
                    </div>
                    <p className="subscription-card-message">{message}</p>
                  </div>

                  {!unsubscribeRevealed && (
                    <div className="unsub-action">
                      <p className="unsub-action-hint">{t('Чтобы отменить подписку', 'Жазылуудан баш тартуу үчүн', 'To cancel the subscription')}</p>
                      <button type="button" className="link-button" onClick={() => { if (!revealPending) showUnsubscribeReveal(); }} disabled={revealPending}>
                        {revealPending
                          ? t('подождите...', 'күтө туруңуз...', 'please wait...')
                          : t('нажмите здесь', 'бул жерди басыңыз', 'click here')}
                        {revealPending && <span className="spinner" style={{ display: 'inline-flex' }} />}
                      </button>
                    </div>
                  )}

                  {unsubscribeRevealed && (
                    <div className="unsub-action">
                      <button type="button" className="cancel-subscription-btn" onClick={openConfirmPopup}>
                        {t('Отменить подписку на Kid Security', 'Kid Security кызматынан баш тартуу', 'Cancel the Kid Security subscription')}
                      </button>
                    </div>
                  )}

                  {showPopup && (
                    <div className="popup">
                      <div className="popup-content">
                        <div className="popup-icon">
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="10" stroke="#e0574c" strokeWidth="1.8" />
                            <path d="M12 7.5v6" stroke="#e0574c" strokeWidth="1.8" strokeLinecap="round" />
                            <circle cx="12" cy="16.5" r="1" fill="#e0574c" />
                          </svg>
                        </div>
                        <p>{t('Вы уверены, что хотите отменить подписку на Kid Security?', 'Kid Security кызматынан баш тартууну каалайсызбы?', 'Are you sure you want to cancel your Kid Security subscription?')}</p>
                        <button id="unsub-cancel-btn" className="popup-btn popup-btn-keep" onClick={cancelUnsubscribe} disabled={confirmLoading}>
                          {t('Оставить подписку', 'Жазылууну калтыруу', 'Keep subscription')}
                        </button>
                        <button id="unsub-confirm-btn" className="popup-btn popup-btn-danger" onClick={confirmUnsubscribe} disabled={confirmLoading || confirmSucceeded}>
                          {confirmLoading
                            ? t('Отмена...', 'Жокко чыгарылууда...', 'Cancelling...')
                            : t('Отменить подписку', 'Жазылуудан баш тартуу', 'Cancel subscription')}
                        </button>
                        <div className={`warning-msg ${confirmMessage ? 'active' : ''}`}>
                          <span>{confirmMessage}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <div className="title">
                    <h4 id="headup-title">{`+996 ${msisdn}`}</h4>
                  </div>
                  <div className="form-content-middle">
                    <div className="status-icon">
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" stroke="#8a938c" strokeWidth="1.8" />
                        <path d="M12 8v5" stroke="#8a938c" strokeWidth="1.8" strokeLinecap="round" />
                        <circle cx="12" cy="16" r="1" fill="#8a938c" />
                      </svg>
                    </div>
                    <div className="unsub-inactive-msg">
                      <span>{message}</span>
                    </div>
                    <Link to={`/?lang=${language}#subscribe`} className="inactive-cta">
                      {t('Оформить подписку', 'Жазылуу', 'Subscribe')}
                    </Link>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <SiteFooter t={t} language={language} hideUnsubLink />
    </div>
  );
}

export default UnsubPage;
